import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { APP_CONFIG } from '../app-config.constants';
import { IAppConfig } from '../app-config.interface';
import { ObjectResponse } from '../classes/object-response';
import { CacheService } from '../services/cache.service';
import { map } from 'rxjs/operators';
import { ApiConfigService } from './api-config/api-config.service';
import { ILocationDetails } from '../classes/points-of-interest';

@Injectable()
export class PointsOfInterestService {

  defaultIcon = 'star_rate';
  qualitiesIcons = new Map<string, string>([
    ['quiet', 'volume_off'],
    ['car friendly', 'directions_car'],
    ['pedestrian friendly', 'directions_walk'],
    ['cafes', 'local_cafe'],
    ['shopping', 'local_mall'],
    ['groceries', 'local_grocery_store'],
    ['nightlife', 'local_bar'],
    ['restaurants', 'restaurant'],
    ['high schools', 'school'],
    ['daycares', 'child_care'],
    ['transit friendly', 'directions_subway'],
    ['parks', 'nature'],
    ['greenery', 'park'],
    ['elementary schools', 'face'],
    ['vibrant', 'celebration'],
    ['cycling friendly', 'pedal_bike']
  ]);

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
      private http: HttpClient,
      private cache: CacheService,
      private apiConfigService: ApiConfigService
    ) {
  }

  // Get the mapped icon for a category, falling back to default if no map exists
  getScoreIcon(category: string) {
    if (this.qualitiesIcons.has(category.toLowerCase()))
      return this.qualitiesIcons.get(category.toLowerCase());
    return this.defaultIcon;
  }

  private getBaseUrl(route: string): string {
    return this.apiConfigService.getServerRoot(route);
  }

  private sanitizeAnchors(html: string): string {
    if (html) {
      return html.replace(/<a /g, '<a rel="noopener" ');
    }
  }

  /**
   * Used to get the score and POI data from local logic
   *
   * @param lat latitude for a location
   * @param lng longitude for a location
   *
   * @returns an object containing ordered scores and points of interest
   */
  async getLocationDetails(lat: number, lng: number): Promise<ILocationDetails> {
    const base = this.getBaseUrl(this.appConfig.urls.poi.base);
    const route = this.appConfig.urls.poi.base;
    const params = new HttpParams()
      .set('lat', lat.toString())
      .set('lng', lng.toString());

    const hashKey = this.cache.getRequestHash(route, params);
    const hashValue = this.cache.get<ILocationDetails>(hashKey);
    if (hashValue) {
      return new Promise<ILocationDetails>((resolve) => {
        resolve(hashValue);
      });
    }

    return await this.http.get<ObjectResponse<ILocationDetails>>(base + route, { params: params, withCredentials: false })
      .pipe(map(res => {
        // Add the category icons to the object
        const locationDetails = res.result;
        locationDetails.scores.forEach(scores => scores.icon = this.getScoreIcon(scores.name));
        locationDetails.attributionHtml = this.sanitizeAnchors(locationDetails.attributionHtml);
        this.cache.set(hashKey, locationDetails, 300);
        return locationDetails;
      }))
      .toPromise();
  }
}
